import React, { useState, useRef, useEffect } from "react";
import styles from "./FrameWrapperPdf.module.scss";
import IconClose from "../../common/icons/IconClose";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker.entry";
import "@react-pdf-viewer/core/lib/styles/index.css";
import stringSimilarity from "string-similarity";

const PDF_WORKER_URL =
  "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";

const normalize = (str) =>
  str
    .toLowerCase()
    .replace(/[\s’'`´]+/g, " ")
    .replace(/[.,;:!?()]/g, "")
    .trim();

const FrameWrapperPdf = ({
  closePopup,
  PdfUrl,
  page,
  content = [],
  highlited_pages = [],
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const viewerRef = useRef();

  useEffect(() => {
    if (!PdfUrl) return;

    let cancelled = false;
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;

    const snippetsByPage = {};

    if (Array.isArray(content) && page) {
      snippetsByPage[page] = content.map(normalize);
    }

    highlited_pages.forEach(({ extrait_page, content }) => {
      if (Array.isArray(content) && extrait_page) {
        const normalizedList = content.map(normalize);
        snippetsByPage[extrait_page] = normalizedList;
      }
    });

    const highlightSnippet = (
      pdfPage,
      textContent,
      width,
      height,
      pageLayer,
      snippet
    ) => {
      let positions = [];
      let fullText = "";

      textContent.items.forEach((item, idx) => {
        const start = fullText.length;
        fullText += item.str + " ";
        const end = fullText.length;
        positions.push({ idx, start, end, item });
      });

      const normalizedFullText = normalize(fullText);

      let bestMatch = { rating: 0, target: "", index: -1 };

      for (let i = 0; i <= normalizedFullText.length - snippet.length; i++) {
        const window = normalizedFullText.slice(i, i + snippet.length);
        const rating = stringSimilarity.compareTwoStrings(window, snippet);
        if (rating > bestMatch.rating) {
          bestMatch = { rating, target: window, index: i };
        }
      }

      if (bestMatch.rating > 0.75) {
        const matchIdx = bestMatch.index;
        const matchEnd = matchIdx + bestMatch.target.length;

        positions.forEach(({ start, end, item }) => {
          if (end > matchIdx && start < matchEnd && item.transform) {
            const [a, b, c, d, e, f] = item.transform;
            const fontHeight = Math.abs(d);
            const left = (e / pdfPage.view[2]) * width;
            const top =
              ((pdfPage.view[3] - f) / pdfPage.view[3]) * height -
              fontHeight * 1.4;
            const spanWidth = (item.width / pdfPage.view[2]) * width;
            const spanHeight = (fontHeight / pdfPage.view[3]) * height * 0.9;

            const highlight = document.createElement("div");
            highlight.className = "highlight-text-span";
            Object.assign(highlight.style, {
              position: "absolute",
              left: `${left}px`,
              top: `${top}px`,
              width: `${spanWidth}px`,
              height: `${spanHeight}px`,
              background: "rgba(255, 255, 0, 0.2)",
              mixBlendMode: "multiply",
              pointerEvents: "none",
              zIndex: 5,
            });

            pageLayer.appendChild(highlight);
          }
        });
      } else {
        console.warn("❗ No match found for snippet:", snippet.slice(0, 50));
      }
    };

    const runHighlight = () => {
      const requestedPages = Object.keys(snippetsByPage)
        .map(Number)
        .filter((p) => !isNaN(p) && p > 0 && Number.isFinite(p));

      if (requestedPages.length === 0) {
        return;
      }

      pdfjsLib
        .getDocument(PdfUrl)
        .promise.then((doc) => {
          const numPages = doc.numPages;
          const validPages = requestedPages.filter((p) => p <= numPages);
          
          if (validPages.length === 0) {
            console.warn("No valid pages to highlight - requested pages exceed document length");
            return Promise.resolve([]);
          }

          return Promise.all(
            validPages.map((targetPage) =>
              doc.getPage(targetPage).then((pdfPage) =>
                pdfPage.getTextContent().then((textContent) => ({
                  targetPage,
                  pdfPage,
                  textContent,
                }))
              )
            )
          );
        })
        .then((results) => {
          if (cancelled || !results || results.length === 0) return;

          setTimeout(() => {
            results.forEach(({ targetPage, pdfPage, textContent }) => {
              const pageLayers = document.querySelectorAll(
                ".rpv-core__page-layer"
              );
              const pageLayer = pageLayers[targetPage - 1];
              if (!pageLayer) return;

              const textLayer = pageLayer.querySelector(
                ".rpv-core__text-layer"
              );
              if (!textLayer) return;

              const waitForLayer = () => {
                const { width, height } = textLayer.getBoundingClientRect();
                if (width < 10 || height < 10) {
                  setTimeout(waitForLayer, 200);
                  return;
                }

                pageLayer
                  .querySelectorAll(".highlight-text-span")
                  .forEach((el) => el.remove());

                snippetsByPage[targetPage].forEach((snippet) => {
                  highlightSnippet(
                    pdfPage,
                    textContent,
                    width,
                    height,
                    pageLayer,
                    snippet
                  );
                });
              };

              waitForLayer();
            });
          }, 600);
        });
    };

    runHighlight();

    return () => {
      cancelled = true;
    };
  }, [PdfUrl, page, content, highlited_pages]);

  const validInitialPage = (page && !isNaN(page) && page > 0) ? page - 1 : 0;

  return (
    <>
      <div className={styles.closeIcon} onClick={closePopup}>
        <IconClose />
      </div>

      <div className={styles.pdfViewer} onClick={(e) => e.stopPropagation()}>
        <Worker workerUrl={PDF_WORKER_URL}>
          {isLoading ? (
            <div className={styles.loader} />
          ) : (
            <Viewer
              ref={viewerRef}
              fileUrl={PdfUrl}
              initialPage={validInitialPage}
            />
          )}
        </Worker>
      </div>
    </>
  );
};

export default FrameWrapperPdf;
